import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface PostReaction {
  id: string;
  post_id: string;
  user_id: string;
  type: 'like' | 'heart';
  created_at: string;
}

interface ReactionCounts {
  likes: number;
  hearts: number;
}

export function usePostReactions(postId: string | undefined) {
  const [reactions, setReactions] = useState<PostReaction[]>([]);
  const [userReactions, setUserReactions] = useState<{ like: boolean; heart: boolean }>({ like: false, heart: false });
  const [counts, setCounts] = useState<ReactionCounts>({ likes: 0, hearts: 0 });
  const [loading, setLoading] = useState(false);

  const fetchReactions = useCallback(async () => {
    if (!postId) return;
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('post_reactions')
        .select('*')
        .eq('post_id', postId);

      if (err) throw err;
      const all = (data ?? []) as PostReaction[];
      setReactions(all);

      const likes = all.filter(r => r.type === 'like').length;
      const hearts = all.filter(r => r.type === 'heart').length;
      setCounts({ likes, hearts });

      const { data: userData } = await supabase.auth.getUser();
      const uid = userData?.user?.id;
      if (uid) {
        setUserReactions({
          like: all.some(r => r.user_id === uid && r.type === 'like'),
          heart: all.some(r => r.user_id === uid && r.type === 'heart'),
        });
      }
    } catch {
      // silent fail for reactions
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchReactions();
  }, [fetchReactions]);

  const toggleReaction = async (type: 'like' | 'heart'): Promise<void> => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      if (!user) return;

      const hasReacted = userReactions[type];

      if (hasReacted) {
        await supabase
          .from('post_reactions')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', user.id)
          .eq('type', type);
      } else {
        await supabase
          .from('post_reactions')
          .insert({
            post_id: postId,
            user_id: user.id,
            type,
          });
      }

      await fetchReactions();
    } catch {
      // silent
    }
  };

  return { reactions, userReactions, counts, loading, toggleReaction, fetchReactions };
}

export function usePostReactionsStats() {
  const [stats, setStats] = useState<Record<string, { likes: number; hearts: number }>>({});
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('post_reactions')
        .select('*');

      if (err) throw err;
      const all = (data ?? []) as PostReaction[];

      const grouped: Record<string, { likes: number; hearts: number }> = {};
      all.forEach(r => {
        if (!grouped[r.post_id]) grouped[r.post_id] = { likes: 0, hearts: 0 };
        if (r.type === 'like') grouped[r.post_id].likes++;
        else grouped[r.post_id].hearts++;
      });
      setStats(grouped);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { stats, loading, fetchAll };
}